import { useEffect, useMemo } from 'react';
import { CircularProgress, Skeleton, Stack, useTheme } from '@mui/material';

import Button from '@components/Button';
import { CustomSelect } from '@components/CustomSelect';
import ErrorMessage from '@components/ErrorMessage';
import { NestedMenu } from '@components/NestedMenu';
import { useInputStore } from '@store/inputStore';
import { useLocationStore } from '@store/locationStore';
import { useMessagesStore } from '@store/messagesStore';
import { useRoleStore } from '@store/roleStore';
import { useGeneralPredefinedPrompts, usePredefinedPromptsByRole } from '@services/queries';

import { additionalFeaturesTexts } from '../constants';
import { useDownloadChatHistory, useSupportedCountries } from '../services/queries';

const selectRoleConfig = {
    minWidth: 220,
    height: 36,
} as const;

const SelectRole = () => {
    const theme = useTheme();
    const { data, isLoading, isError, error } = usePredefinedPromptsByRole();
    const role = useRoleStore((state) => state.role);
    const setRole = useRoleStore((state) => state.setRole);

    const dataWithRoleAsValue = useMemo(
        () =>
            data?.map(({ role, ...rest }) => ({
                value: role,
                ...rest,
            })) ?? [],
        [data]
    );

    if (isError) return <ErrorMessage error={error} />;

    if (isLoading)
        return (
            <Skeleton
                variant='rounded'
                height={selectRoleConfig.height}
                width={selectRoleConfig.minWidth}
                sx={{
                    borderRadius: theme.borderRadius.semiLarge,
                    borderBottomRightRadius: 0,
                }}
            />
        );

    if (!data) return null;

    return (
        <CustomSelect
            data={dataWithRoleAsValue}
            placeholder={additionalFeaturesTexts.rolePlaceholder}
            currentValueId={role?.id}
            setCurrentValue={setRole}
            config={selectRoleConfig}
        />
    );
};

const selectCountryConfig = {
    minWidth: 174,
    height: 36,
} as const;

const SelectCountry = () => {
    const theme = useTheme();
    const { data, isLoading, isError, error } = useSupportedCountries();
    const location = useLocationStore((state) => state.location);
    const setLocation = useLocationStore((state) => state.setLocation);

    useEffect(() => {
        const defaultLocation = data?.find((item) => !item.isCountry);
        if (defaultLocation) {
            setLocation(defaultLocation);
        }
    }, [data, setLocation]);

    if (isError) return <ErrorMessage error={error} />;

    if (isLoading)
        return (
            <Skeleton
                variant='rounded'
                height={selectCountryConfig.height}
                width={selectCountryConfig.minWidth}
                sx={{
                    borderRadius: theme.borderRadius.semiLarge,
                    borderBottomRightRadius: 0,
                }}
            />
        );

    if (!data) return null;

    return (
        <CustomSelect
            data={data}
            placeholder={additionalFeaturesTexts.countryPlaceholder}
            currentValueId={location?.id}
            setCurrentValue={setLocation}
            config={selectCountryConfig}
        />
    );
};

const selectPredefinedPromptConfig = {
    minWidth: 180,
    height: 36,
} as const;

const SelectPredefinedPrompt = () => {
    const theme = useTheme();
    const { data, isLoading, isError, error } = useGeneralPredefinedPrompts();
    const role = useRoleStore((state) => state.role);
    const setInputValue = useInputStore((state) => state.setInputValue);

    if (isError) return <ErrorMessage error={error} />;

    if (isLoading)
        return (
            <Skeleton
                variant='rounded'
                height={selectPredefinedPromptConfig.height}
                width={selectPredefinedPromptConfig.minWidth}
                sx={{
                    borderRadius: theme.borderRadius.semiLarge,
                    borderBottomRightRadius: 0,
                }}
            />
        );

    if (!data) return null;

    const promptsToSelect = data.map(({ group, id, prompts }) => ({
        group,
        id,
        innerData: prompts.map(({ label, text, promptId }) => ({
            label,
            value: text,
            id: promptId,
        })),
    }));

    if (role) {
        promptsToSelect.push({
            group: role.value,
            id: role.id,
            innerData: role.prompts.map(({ label, text, promptId }) => ({
                label,
                value: text,
                id: promptId,
            })),
        });
    }

    return (
        <NestedMenu
            data={promptsToSelect}
            label={additionalFeaturesTexts.predefinedPromptsPlaceholder}
            config={selectPredefinedPromptConfig}
            handleItemClick={setInputValue}
        />
    );
};

const ExportChatToMSWorld = () => {
    const theme = useTheme();
    const messages = useMessagesStore((state) => state.messages);
    const { mutate: downloadChatHistory, isPending, error, isError } = useDownloadChatHistory();

    const hasMessages = messages.length > 0;
    const isDisabled = isPending || !hasMessages;

    return (
        <Stack direction='row' alignItems='center' gap={2}>
            {isError && <ErrorMessage error={error} />}
            {isPending && (
                <CircularProgress size={24} sx={{ color: theme.palette.customColors.purple }} />
            )}
            <Button
                variant='contained'
                color='primary'
                iconType='download'
                lighterFont
                onClick={() => downloadChatHistory(messages)}
                disabled={isDisabled}
            >
                {additionalFeaturesTexts.exportToMSWorld}
            </Button>
        </Stack>
    );
};

export const AdditionalFeatures = () => (
    <Stack direction='row' justifyContent='space-between' px={1} pb={1}>
        <Stack direction='row' gap={1}>
            <SelectRole />
            <SelectCountry />
            <SelectPredefinedPrompt />
        </Stack>
        <ExportChatToMSWorld />
    </Stack>
);
